// src/pages/Validation.js
import React, { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { requestService } from '../services/requestService';
import './Validation.css';

const Validation = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedOffer, setSelectedOffer] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await requestService.getAdminRequests(
        selectedStatus !== 'all' ? selectedStatus : null,
        selectedOffer !== 'all' ? selectedOffer : null
      );
      
      setRequests(data || []);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des demandes');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [selectedStatus, selectedOffer]);

  const handleApproveRequest = async (requestId) => {
    if (!window.confirm('Approuver cette demande ?')) return;
    try {
      await requestService.approveRequest(requestId);
      fetchRequests();
      alert('✅ Demande approuvée');
    } catch (err) {
      setError('Erreur lors de la validation');
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!window.confirm('Rejeter cette demande ?')) return;
    try {
      await requestService.rejectRequest(requestId);
      fetchRequests();
      alert('❌ Demande rejetée');
    } catch (err) {
      setError('Erreur lors du rejet');
    }
  };

  const getStatusInfo = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', color: '#f59e0b', bg: '#fef3c7', icon: '⏳' },
      approved: { label: 'Approuvée', color: '#10b981', bg: '#d1fae5', icon: '✅' },
      rejected: { label: 'Rejetée', color: '#ef4444', bg: '#fee2e2', icon: '❌' },
      completed: { label: 'Terminée', color: '#667eea', bg: '#e0e7ff', icon: '🎯' }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const uniqueOffers = [...new Map(requests.map(req => [req.offer?._id, req.offer])).values()];

  if (loading) {
    return (
      <div className="validation">
        <div className="page-header">
          <div className="page-title">
            <h1>Validation des Demandes</h1>
          </div>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="validation">
      <div className="page-header">
        <div className="page-title">
          <h1>Validation des Demandes</h1>
          <p>Traitez les demandes de vos employés ({requests.length} demande{requests.length > 1 ? 's' : ''})</p>
        </div>
        
        <div className="header-actions">
          <div className="filters">
            <select 
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">📋 Tous les statuts</option>
              <option value="pending">⏳ En attente</option>
              <option value="approved">✅ Approuvées</option>
              <option value="rejected">❌ Rejetées</option>
            </select>

            <select 
              value={selectedOffer}
              onChange={(e) => setSelectedOffer(e.target.value)}
              className="filter-select"
            >
              <option value="all">🎁 Toutes les offres</option>
              {uniqueOffers.map(offer => (
                <option key={offer._id} value={offer._id}>
                  {offer.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <Card className="error-card" padding="md">
          <div className="error-message">❌ {error}</div>
        </Card>
      )}

      {/* TABLEAU DES DEMANDES */}
      <div className="validation-table-container">
        <table className="validation-table">
          <thead>
            <tr>
              <th>Employé</th>
              <th>Offre</th>
              <th>Code offre</th>
              <th>Organisateur</th>
              <th>Statut</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map(request => {
              const { offer, user } = request;
              const statusInfo = getStatusInfo(request.status);
              
              return (
                <tr key={request._id}>
                  <td data-label="Employé">
                    <div className="employee-cell">
                      <strong>{user?.firstName} {user?.lastName}</strong>
                      <small>{user?.employeeId}</small>
                    </div>
                  </td>
                  <td data-label="Offre">
                    <div className="offer-cell">
                      <strong>{offer?.title}</strong>
                    </div>
                  </td>
                  <td data-label="Code offre">
                    <code className="offer-code-cell">{offer?.codeOffre}</code>
                  </td>
                  <td data-label="Organisateur">
                    <span className="partner-name">{offer?.partnerName || '-'}</span>
                  </td>
                  <td data-label="Statut">
                    <span className="status-badge" style={{ backgroundColor: statusInfo.bg, color: statusInfo.color }}>
                      {statusInfo.icon} {statusInfo.label}
                    </span>
                  </td>
                  <td data-label="Date">{formatDate(request.createdAt)}</td>
                  <td data-label="Actions">
                    <div className="actions-cell">
                      {request.status === 'pending' && (
                        <>
                          <button className="action-btn approve" onClick={() => handleApproveRequest(request._id)} title="Approuver">
                            ✅
                          </button>
                          <button className="action-btn reject" onClick={() => handleRejectRequest(request._id)} title="Rejeter">
                            ❌
                          </button>
                        </>
                      )}
                      <button className="action-btn view" onClick={() => setSelectedRequest(request)} title="Voir détails">
                        👁️
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {requests.length === 0 && !loading && (
        <Card className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>Aucune demande trouvée</h3>
          <Button variant="primary" onClick={() => {
            setSelectedStatus('all');
            setSelectedOffer('all');
          }}>
            📋 Voir toutes les demandes
          </Button>
        </Card>
      )}

      {/* MODAL DÉTAILS */}
      {selectedRequest && (
        <div className="modal-overlay" onClick={() => setSelectedRequest(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Détails de la demande</h2>
              <button className="modal-close" onClick={() => setSelectedRequest(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <label>Employé:</label>
                <span><strong>{selectedRequest.user?.firstName} {selectedRequest.user?.lastName}</strong> ({selectedRequest.user?.employeeId})</span>
              </div>
              <div className="detail-row">
                <label>Email:</label>
                <span>{selectedRequest.user?.email}</span>
              </div>
              <div className="detail-row">
                <label>Offre:</label>
                <span><strong>{selectedRequest.offer?.title}</strong> ({selectedRequest.offer?.codeOffre})</span>
              </div>
              <div className="detail-row">
                <label>Organisateur:</label>
                <span>{selectedRequest.offer?.partnerName}</span>
              </div>
              <div className="detail-row">
                <label>Description:</label>
                <div className="detail-message">{selectedRequest.offer?.description}</div>
              </div>
              <div className="detail-row">
                <label>Date demande:</label>
                <span>{formatDate(selectedRequest.createdAt)}</span>
              </div>
              
              {selectedRequest.formData && Object.keys(selectedRequest.formData).length > 0 && (
                <div className="detail-section">
                  <h4>📝 Informations complémentaires</h4>
                  {Object.entries(selectedRequest.formData).map(([key, value]) => (
                    <div key={key} className="detail-row">
                      <label>{key}:</label>
                      <span>{value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {selectedRequest.status === 'pending' && (
                <>
                  <button className="modal-action-btn approve" onClick={() => {
                    handleApproveRequest(selectedRequest._id);
                    setSelectedRequest(null);
                  }}>✅ Approuver</button>
                  <button className="modal-action-btn reject" onClick={() => {
                    handleRejectRequest(selectedRequest._id);
                    setSelectedRequest(null);
                  }}>❌ Rejeter</button>
                </>
              )}
              <button className="modal-action-btn close" onClick={() => setSelectedRequest(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Validation;